import {
  renderBulletButton,
  syncBulletCurrent,
} from "../helpers/paginationBullets";

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.activeVideos = [];

    window.loadSwiper.then((swiper) => {
      this.swiperLib = swiper;
      this._initThumbsCarousel();
      this._initCarousel();
    });
  }

  connectedCallback() {
    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-video-control-button]")) return;

      const thumbnail = e.target.closest("[data-thumbnail-index]");
      if (thumbnail) {
        this._selectThumbnail(parseInt(thumbnail.dataset.thumbnailIndex));
        return;
      }

      const zoomButton = e.target.closest("[data-open-zoom]");
      if (zoomButton) {
        this._openZoomGallery(zoomButton);
      }
    });

    this.addEventListener("keyup", (e) => {
      if (
        !("thumbnailIndex" in e.target.dataset) ||
        (e.key !== "Enter" && e.key !== " ")
      )
        return;

      this._selectThumbnail(parseInt(e.target.dataset.thumbnailIndex));
    });
  }

  disconnectedCallback() {}

  _initThumbsCarousel() {
    if (this.thumbsSwiper) return;

    const container = this.querySelector(
      `.${this._prefix}__thumbnails-container`,
    );

    if (!container) return;

    const { Swiper, Mousewheel, Navigation } = this.swiperLib;

    this._toggleSwiperClasses(true);

    const swiperParams = {
      modules: [Mousewheel, Navigation],
      slidesPerView: "auto",
      slidesPerGroupAuto: true,
      watchSlidesProgress: true,
      mousewheel: true,
      spaceBetween: 8,
      navigation: {
        nextEl: this.querySelector("[data-thumb-navigation-next]"),
        prevEl: this.querySelector("[data-thumb-navigation-previous]"),
      },
    };

    this.thumbsSwiper = new Swiper(container, swiperParams);
  }

  _initCarousel() {
    const { Swiper, Navigation, Pagination } = this.swiperLib;

    if (this.gallerySwiper) return;

    this._toggleSwiperClasses(true);

    const updateCurrentSlide = () => {
      if (!this.thumbsSwiper || !this.gallerySwiper) return;

      const index = this.gallerySwiper.realIndex;
      const activeSlide = this.gallerySwiper.slides[index];

      this.thumbsSwiper.slides.forEach((slide, i) => {
        const isActive = i === index;
        const button = slide.querySelector("button");

        slide.classList.toggle("swiper-slide-thumb-active", isActive);

        if (isActive) {
          button?.setAttribute("aria-current", "true");
        } else {
          button?.removeAttribute("aria-current");
        }
      });

      // Keep the active thumbnail in view (replaces native thumbs auto-scroll)
      const activeThumb = this.thumbsSwiper.slides[index];
      if (
        activeThumb &&
        !activeThumb.classList.contains("swiper-slide-visible")
      ) {
        this.thumbsSwiper.slideTo(index);
      }

      this.gallerySwiper.slides.forEach((slide) => {
        if (slide == activeSlide) {
          activeSlide.removeAttribute("aria-hidden");
          activeSlide.querySelectorAll("a, button").forEach((el) => {
            el.tabIndex = null;
          });
        } else {
          slide.setAttribute("aria-hidden", "true");
          slide.querySelectorAll("a, button").forEach((el) => {
            el.tabIndex = -1;
          });
        }
      });
    };

    let swiperParams = {
      modules: [Navigation, Pagination],
      slidesPerView: "auto",
      spaceBetween: 8,
      loop: false,
      speed: 400,
      watchSlidesProgress: true,
      centerInsufficientSlides: true,
      navigation: {
        nextEl: this.querySelector("[data-navigation-next]"),
        prevEl: this.querySelector("[data-navigation-previous]"),
      },
    };

    if (this.thumbsSwiper) {
      swiperParams = {
        ...swiperParams,
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
          renderBullet: renderBulletButton,
        },
        on: {
          realIndexChange: () => updateCurrentSlide(),
          paginationUpdate: syncBulletCurrent,
        },
      };
    }

    this.gallerySwiper = new Swiper(
      this.querySelector(`.${this._prefix}__media-container`),
      swiperParams,
    );

    updateCurrentSlide();

    this.gallerySwiper.on("slideChange", () => {
      const visibleSlides = this.gallerySwiper.slides.filter((slide) =>
        slide.classList.contains("swiper-slide-visible"),
      );
      this._selectVideo(visibleSlides);
    });

    this.gallerySwiper.on("click", (_, e) => {
      if (e.target.closest("[data-video-control-button]")) return;

      this._openZoomGallery(this.querySelector("a, button"));
    });
  }

  _selectThumbnail(index) {
    if (Number.isNaN(index)) return;

    // Driving the main swiper fires realIndexChange, which syncs the
    // active thumbnail back via updateCurrentSlide().
    this.gallerySwiper?.slideTo(index, 400);
  }

  _openZoomGallery(trigger) {
    const zoomGallery = this._zoomMarkup?.content.children[0];

    if (!zoomGallery) return;

    zoomGallery.dataset.preselectMediaIndex =
      this.gallerySwiper?.realIndex || 0;

    const html = zoomGallery.outerHTML;
    (trigger || this).dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: { html, target: "zoom-modal" },
      }),
    );
  }

  _destroyCarousel() {
    if (!this.gallerySwiper) return;

    this.gallerySwiper.destroy(true, true);
    this.gallerySwiper = null;
    this._toggleSwiperClasses(false);
  }

  _toggleSwiperClasses(add) {
    this.querySelectorAll("[data-swiper-class]").forEach((el) => {
      el.classList.toggle(el.dataset.swiperClass, add);
    });
  }

  _selectVideo(els) {
    this.activeVideos.forEach((video) => video.pause());

    this.activeVideos = [];

    els.forEach((el) => {
      const video = el.querySelector("video");

      if (video) {
        this.activeVideos.push(video);
        video.play();
      }
    });
  }

  get _zoomMarkup() {
    return this.querySelector("[data-zoom-markup]");
  }

  get _prefix() {
    return this.dataset.classPrefix;
  }

  get _isMobile() {
    return window.matchMedia("(max-width: 1023px)").matches;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
